import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { cursusApi, studentsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { GraduationCap, Plus, Trash, ArrowLeft } from '@phosphor-icons/react';
import { toast } from 'sonner';
import StudentTimeline from '../components/students/StudentTimeline';

const OUTCOME_LABEL = {
  promoted: { fr: 'Promu(e)', en: 'Promoted', color: 'bg-green-100 text-green-700' },
  repeated: { fr: 'Redoublement', en: 'Repeated', color: 'bg-yellow-100 text-yellow-700' },
  transferred: { fr: 'Transféré(e)', en: 'Transferred', color: 'bg-blue-100 text-blue-700' },
  left: { fr: 'Parti(e)', en: 'Left', color: 'bg-red-100 text-red-700' },
};

const Cursus = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [student, setStudent] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ school_year: '', class_name: '', average: '', rank: '', outcome: 'promoted', comment: '' });

  useEffect(() => {
    loadAll();
  }, [studentId]);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [sRes, cRes] = await Promise.all([
        studentsApi.getById(studentId),
        cursusApi.getByStudent(studentId)
      ]);
      setStudent(sRes.data);
      setEntries(cRes.data || []);
    } catch {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await cursusApi.upsert(studentId, {
        school_year: form.school_year,
        class_name: form.class_name,
        average: form.average === '' ? null : Number(form.average),
        rank: form.rank === '' ? null : Number(form.rank),
        outcome: form.outcome,
        comment: form.comment
      });
      toast.success(t('success'));
      setDialogOpen(false);
      setForm({ school_year: '', class_name: '', average: '', rank: '', outcome: 'promoted', comment: '' });
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('error'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await cursusApi.delete(id);
      toast.success(t('success'));
      await loadAll();
    } catch {
      toast.error(t('error'));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="cursus-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/students')} data-testid="back-btn"><ArrowLeft size={20} /></Button>
          <GraduationCap size={28} className="text-[#0052FF]" weight="duotone" />
          <div>
            <h1 className="font-outfit text-2xl font-bold text-[#0F172A]">{t('cursus')}</h1>
            {student && <p className="text-sm text-[#64748B]">{student.first_name} {student.last_name} — {student.class_id}</p>}
          </div>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#0052FF] hover:bg-[#0040D9]" data-testid="add-cursus-btn">
          <Plus size={18} className="mr-2" />{t('addCursusEntry')}
        </Button>
      </div>

      {/* Auto-computed timeline (cursus + year-end pipeline) */}
      <StudentTimeline studentId={studentId} />

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('schoolYear')}</TableHead>
                <TableHead>{t('class')}</TableHead>
                <TableHead className="text-right">{t('average')}</TableHead>
                <TableHead className="text-right">{t('rank')}</TableHead>
                <TableHead>{t('outcome')}</TableHead>
                <TableHead>{language === 'fr' ? 'Commentaire' : 'Comment'}</TableHead>
                <TableHead className="text-right">{t('actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#64748B]">{t('noData')}</TableCell></TableRow>
              ) : (
                entries.map((e) => (
                  <TableRow key={e.id} data-testid={`cursus-row-${e.school_year}`}>
                    <TableCell className="font-mono">{e.school_year}</TableCell>
                    <TableCell className="font-medium">{e.class_name}</TableCell>
                    <TableCell className="text-right">{e.average != null ? `${Number(e.average).toFixed(2)}/20` : '—'}</TableCell>
                    <TableCell className="text-right">{e.rank ?? '—'}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${OUTCOME_LABEL[e.outcome]?.color || ''}`}>
                        {OUTCOME_LABEL[e.outcome]?.[language] || e.outcome}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">{e.comment}</TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(e.id)} data-testid={`delete-cursus-${e.school_year}`}>
                        <Trash size={16} />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">{t('addCursusEntry')}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('schoolYear')}</Label>
                <Input data-testid="cursus-year" placeholder="2024-2025" value={form.school_year} onChange={(e) => setForm({ ...form, school_year: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>{t('class')}</Label>
                <Input data-testid="cursus-class" placeholder="CE1" value={form.class_name} onChange={(e) => setForm({ ...form, class_name: e.target.value })} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('average')} /20</Label>
                <Input data-testid="cursus-average" type="number" step="0.01" min="0" max="20" value={form.average} onChange={(e) => setForm({ ...form, average: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>{t('rank')}</Label>
                <Input data-testid="cursus-rank" type="number" min="1" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('outcome')}</Label>
              <Select value={form.outcome} onValueChange={(v) => setForm({ ...form, outcome: v })}>
                <SelectTrigger data-testid="cursus-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="promoted">{OUTCOME_LABEL.promoted[language]}</SelectItem>
                  <SelectItem value="repeated">{OUTCOME_LABEL.repeated[language]}</SelectItem>
                  <SelectItem value="transferred">{OUTCOME_LABEL.transferred[language]}</SelectItem>
                  <SelectItem value="left">{OUTCOME_LABEL.left[language]}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Commentaire' : 'Comment'}</Label>
              <Textarea data-testid="cursus-comment" value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={3} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
              <Button type="submit" data-testid="save-cursus-btn" className="bg-[#0052FF] hover:bg-[#0040D9]">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cursus;
